// Modules
import React, { useEffect, useReducer } from 'react'
import { render } from 'react-dom'
import { BrowserRouter as Router } from 'react-router-dom'
import { initReactI18next } from 'react-i18next'
import TagManager from 'react-gtm-module'
import { FocusStyleManager } from '@blueprintjs/core'
import { ApolloClient, ApolloLink, ApolloProvider, from, HttpLink, InMemoryCache } from '@apollo/client'
import { createBrowserHistory } from 'history'
import i18n from 'i18next'

// Routes
import Protected from 'routes/Protected'
import Unprotected from 'routes/Unprotected'

// Components
import Notice from 'components/utility/notice/Notice'

// Context
import Data from 'context/Data'
import Dispatch from 'context/Dispatch'

// Reducers
import applicationReducer from 'reducers'

// Helpers
// import { getAndStoreUTMValues, removeStoredUTMValues, removeUTMValuesFromSearchParameters } from 'helpers/analytics'
import { getTeam, getToken, logout } from 'helpers/authentication'
import { checkIfConvertibleToURL } from 'helpers/format'
import { checkIfURLSearchParametersHasKey, getURLSearchParameter, removeURLSearchParameters } from 'helpers/route'
import { getDirection, getLanguage, getTheme, handleStorage } from 'helpers/system'

// Data
import defaultInitialState from 'data/initialState'

// Translations
import translations from 'translations'

// Constants
import { APPLICATION_URL_PATH_PREFIX, DATA_URL, IS_LIVE, TAG_MANAGER_ID } from 'constants/environment'
import { LANGUAGES, THEMES } from 'constants/options'
import { REQUIRED_STORAGE_FIELDS, STORAGE_PREFIX, STORAGE_VERSION } from 'constants/settings'

// Styles
import 'styles/reset.scss'
import 'styles/blueprint/index.scss'
import 'styles/application/index.scss'

// Operations
storeParameters()
removePreviousRedirectURL() // Cleanup
const browserHistory = createBrowserHistory({ basename: APPLICATION_URL_PATH_PREFIX || '/' }) // History
FocusStyleManager.onlyShowFocusOnTabs() // Focus
setStorageSettings() // Storage
TagManager.initialize({ gtmId: TAG_MANAGER_ID }) // Analytics
storeRedirectURL()
const willLogout = checkIfURLSearchParametersHasKey('logout')
if (willLogout) logout()
i18n // Internationalization
	.use(initReactI18next)
	.init({
		defaultNS: 'common',
		initImmediate: false,
		interpolation: { escapeValue: false },
		lng: getLanguage(),
		react: { useSuspense: false },
		resources: translations
	})
// handleUTMParameters()

// GraphQL client
const authorizationLink = new ApolloLink((operation, forward) => {
	operation.setContext(({ headers = {} }) => {
		const token = getToken()
		const authorization = token ? `Bearer ${token.accessToken}` : null
		return { headers: { ...headers, authorization, 'x-team': getTeam() } }
	})
	return forward(operation)
})
const httpLink = new HttpLink({ uri: DATA_URL })
const apolloClient = new ApolloClient({
	cache: new InMemoryCache(),
	connectToDevTools: !IS_LIVE,
	defaultOptions: { watchQuery: { refetchWritePolicy: 'overwrite' } },
	link: from([authorizationLink, httpLink]),
	refetchQueries: { include: ['all'] }
})

// Functions (Local)
/*
function handleUTMParameters () {
	getAndStoreUTMValues()
	removeUTMValuesFromSearchParameters()
}
*/
function removePreviousRedirectURL () {
	localStorage.removeItem('redirectURL')
}
function storeRedirectURL () {
	const redirectURL = getURLSearchParameter('redirect_url')
	if (!!redirectURL && checkIfConvertibleToURL(redirectURL)) {
		localStorage.setItem('redirectURL', redirectURL)
		removeURLSearchParameters(['redirect_url'], { willReplaceCurrent: true })
	}
}
function storeParameters () {
	const queryParameters = new URLSearchParams(window.location.search)
	const language = queryParameters.get('language')
	if (!!language && LANGUAGES.includes(language)) handleStorage('set', 'language', language)
	const theme = queryParameters.get('theme')
	if (!!theme && THEMES.includes(theme)) handleStorage('set', 'theme', theme)
	if (queryParameters.has('token') && queryParameters.has('refresh')) {
		const token = {
			accessToken: queryParameters.get('token'),
			confirmed: true,
			refreshToken: queryParameters.get('refresh')
		}
		handleStorage('set', 'token', JSON.stringify(token))
	}
	const keysToRemove = ['language', 'refresh', 'theme', 'token']
	removeURLSearchParameters(keysToRemove, { willReplaceCurrent: true })
}
function setStorageSettings () {
	localStorage.setItem('storagePrefix', STORAGE_PREFIX)
	localStorage.setItem('storageVersion', STORAGE_VERSION)
	let isVersionChanged = false
	const latestValues = {}
	// Checks if there are required storage items with previous storage version
	if (STORAGE_VERSION > 1) {
		REQUIRED_STORAGE_FIELDS.forEach(field => {
			const currentKey = `${STORAGE_PREFIX}V${STORAGE_VERSION}-${field}`
			const currentValue = localStorage.getItem(currentKey)
			let previousValue
			[...Array(STORAGE_VERSION - 1)].forEach((_, index) => {
				const previousKey = `${STORAGE_PREFIX}V${STORAGE_VERSION - (index + 1)}-${field}`
				if (!previousValue) previousValue = localStorage.getItem(previousKey) // Gets previous value
				localStorage.removeItem(previousKey) // Removes the old value
			})
			if (!!previousValue) {
				if (!isVersionChanged) isVersionChanged = true
				if (!currentValue) latestValues[currentKey] = previousValue // Sets new value
			}
		})
		// Clears all the values if version has changed
		if (isVersionChanged) localStorage.clear()
		// Restores old values
		Object
			.keys(latestValues)
			.forEach(key => localStorage.setItem(key, latestValues[key]))
	}
}
function getInitialState (defaultInitialState) {
	const language = getLanguage()
	const token = getToken()
	return {
		...defaultInitialState,
		direction: getDirection(language),
		isLoggedIn: !!token,
		language,
		theme: getTheme()
	}
}

// Application
function Application () {
	// State
	const [state, dispatch] = useReducer(applicationReducer, defaultInitialState, getInitialState)

	// Effects
	useEffect(setStorageSettings, [])
	useEffect(() => {
		document.documentElement.setAttribute('dir', state.direction)
		document.documentElement.classList[state.direction === 'rtl' ? 'add' : 'remove']('bp4-rtl') // Blueprint
	}, [state.direction])
	useEffect(() => {
		i18n.changeLanguage(state.language)
		document.documentElement.setAttribute('lang', state.language)
		handleStorage('set', 'language', state.language)
	}, [state.language])
	useEffect(() => {
		document.documentElement.setAttribute('data-theme', state.theme)
		document.documentElement.classList[state.theme === 'dark' ? 'add' : 'remove']('bp4-dark') // Blueprint
		document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', state.theme)
		handleStorage('set', 'theme', state.theme)
	}, [state.theme])
	/*
	useEffect(() => {
		window.addEventListener('beforeunload', removeStoredUTMValues)
		return () => window.removeEventListener('beforeunload', removeStoredUTMValues)
	}, [])
	*/

	// Render
	// TODO: Use React.StrictMode later
	if (willLogout) return null
	return (
		<ApolloProvider client={apolloClient}>
			<Router
				history={browserHistory}
				location={browserHistory.location}
				basename={APPLICATION_URL_PATH_PREFIX || '/'}>
				<Dispatch.Provider value={dispatch}>
					<Data.Provider value={state}>
						<Notice />
						{!!state.isLoggedIn
							? <Protected />
							: <Unprotected />
						}
					</Data.Provider>
				</Dispatch.Provider>
			</Router>
		</ApolloProvider>
	)
}

// Execution
render(<Application />, document.getElementById('root'))
